// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { mdiDelete, mdiPencil } from "@mdi/js";
import { Button, Popconfirm } from "antd";
import classnames from "classnames";
import { requestAddNewField } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { useUser } from "hooks";

import { EntityKind } from "models/entityKind";
import { IGroupByListColumn } from "models/groupBy";

import { Tooltip } from "components/base";
import { useGeomBinDispatch } from "components/geom-bin/hooks/useGeomBinDispatch";

import { useGroupByStore } from "./GroupByStoreProvider";
import { selectProps } from "./groupBySelectors";
import { useField, useMutationFieldDelete, useMutationGeomBinDelete } from "./hooks";
import { useProjectPermissions } from "./hooks/useProjectPermissionsContext";

export type UserFieldActionT = {
  field: IGroupByListColumn;
  visible: boolean;
  entityKind: EntityKind;
};

function UserFieldAction({ field, visible, entityKind }: UserFieldActionT) {
  const dispatch = useDispatch();
  const { id, title, fieldType } = field;
  const { isAtLeastPowerUser } = useUser();
  const [showConfirmDeleteOrg, setShowConfirmDeleteOrg] = useState(false);
  const projectId = useSelector(
    (state: RootState) => state.groupBy.selectedProjectIdForColumns
  );

  const { isOrgDefinedColumn, isUserDefinedColumn, isProjectDefinedColumn } =
    useField(field);
  const { mutate: binDelete } = useMutationGeomBinDelete(
    [id],
    isProjectDefinedColumn ? projectId : undefined,
    [field.property]
  );
  const props = useGroupByStore(selectProps);
  const { isLoading, mutate: fieldDelete } = useMutationFieldDelete({
    ids: [id],
    isOrg: isOrgDefinedColumn,
    isProjectColumn: isProjectDefinedColumn,
    entityKind,
    fieldGroups: field.subgroup ? [field.subgroup] : undefined,
    properties: [field.property]
  });

  const geomBinDispatch = useGeomBinDispatch();
  const { permissions: projectPermissions } = useProjectPermissions();

  // derived state
  const isOrgAdmin = isAtLeastPowerUser && isOrgDefinedColumn;

  const hasProjectEditPermission = projectPermissions?.canEditProjectItems ?? false;
  const isProjectEditable = isProjectDefinedColumn && hasProjectEditPermission;
  const isActionAllowed =
    ((isOrgAdmin || isUserDefinedColumn) && props?.editable) || isProjectEditable;

  const isGeom = fieldType === "geom";

  const shouldShowComponent =
    isOrgDefinedColumn || isUserDefinedColumn || isProjectDefinedColumn;

  if (!shouldShowComponent) return null;

  function handleConfirmClick(e) {
    if (!isActionAllowed) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    if (isGeom) {
      binDelete();
    } else {
      fieldDelete();
    }
    e.stopPropagation();
    e.preventDefault();
  }

  function handleCancelClick(e) {
    e.stopPropagation();
    e.preventDefault();
  }

  const handleClick = () => {
    if (!isActionAllowed) return;

    if (field.fieldType === "geom") {
      geomBinDispatch({
        payload: {
          isActive: true,
          selectedGeomBinId: field.id,
          isOrg: field.property.startsWith("Org"),
          projectId: isProjectDefinedColumn ? projectId : undefined,
          isLoading: true
        }
      });
    } else {
      const editType = field.pythonScript ? "Calculated" : "Excel";
      const isOrg = field.property.startsWith("Org");

      dispatch(
        requestAddNewField({
          type: editType,
          isOrg,
          isEdit: true,
          field,
          entityKind: entityKind,
          projectId: isProjectDefinedColumn ? projectId : undefined,
          categoryName: field.group
        })
      );
    }
  };

  const rootClassNames = classnames({ visible: isLoading || visible });

  const EditButton = (
    <Tooltip
      title={
        isActionAllowed
          ? "Edit Field"
          : isProjectDefinedColumn
          ? "Insufficient project permissions to edit this field"
          : "Edit Field"
      }>
      <ActionButton
        shape="circle"
        loading={isLoading}
        disabled={!isActionAllowed}
        onClick={handleClick}
        icon={<Icon path={mdiPencil} size={1.0} />}
      />
    </Tooltip>
  );

  const getDeleteConfirmationMessage = () => {
    const itemType = isGeom ? "bin" : "field";

    return `Please confirm you wish to delete ${itemType} ${title}.`;
  };

  const handleFirstConfirmDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (isOrgDefinedColumn || isProjectDefinedColumn) {
      setShowConfirmDeleteOrg(true);
    } else {
      handleConfirmClick(e);
    }
  };

  const DeleteButton = (
    <Popconfirm
      onConfirm={handleFirstConfirmDelete}
      onCancel={handleCancelClick}
      disabled={!isActionAllowed}
      title={getDeleteConfirmationMessage()}>
      <Tooltip title="Delete Field">
        <ActionButton
          shape="circle"
          danger
          loading={isLoading}
          disabled={!isActionAllowed}
          onClick={(evt) => evt.stopPropagation()}
          icon={<Icon path={mdiDelete} size={1.0} />}
        />
      </Tooltip>
    </Popconfirm>
  );

  return (
    <>
      <Wrapper className={rootClassNames}>
        {EditButton}
        {DeleteButton}
      </Wrapper>

      <Popconfirm
        onConfirm={(e) => {
          handleConfirmClick(e);
          setShowConfirmDeleteOrg(false);
        }}
        onCancel={(e) => {
          setShowConfirmDeleteOrg(false);
          e?.stopPropagation();
          e?.preventDefault();
        }}
        open={showConfirmDeleteOrg}
        onOpenChange={(visible) => setShowConfirmDeleteOrg(visible)}
        title={`This will delete the ${
          isGeom ? "bin" : "field"
        } "${title}" for all users ${
          isOrgDefinedColumn
            ? "in your organization"
            : isProjectDefinedColumn
            ? "with access to this project"
            : ""
        }. Continue?`}
        okText="Delete"
        okType="danger">
        <div />
      </Popconfirm>
    </>
  );
}

export default UserFieldAction;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  opacity: 0;
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  visibility: hidden;

  &.visible {
    opacity: 1;
    pointer-events: all;
    visibility: visible;
  }

  .ant-btn {
    min-width: 22px;
    width: 22px;
    height: 22px;
    align-items: center;
    border: none;
    box-shadow: none;
  }
`;

const ActionButton = styled(Button)`
  color: ${() => "#a2aaad"};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  &:hover {
    color: ${(props) => (props.danger ? "var(--color-danger)" : "var(--color-primary)")};
  }
`;
